import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from './useAuth';

// Journey Context Types
export interface LifeValues {
  relationships: string[];
  personalStories: {
    childStory: string;
    professionalStory: string;
    kindnessReceived: string;
    agencyAtWork: string;
  };
  coreValues: string[];
  authenticMoments: string[];
}

export interface MoneyGoals {
  leverage: {
    timeInvestments: string[];
    valueCreation: string[];
    agencyBuilding: string[];
  };
  lemonadeStandChoices: {
    friendsCalled: string[];
    businessDecisions: string[];
    ownershipPhilosophy: string;
  };
  financialVision: {
    timeFreedom: boolean;
    balanceSheetThinking: boolean;
    socialCapitalFocus: boolean;
  };
}

export interface IntrinsicDrives {
  patterns: string[];
  motivations: string[];
  energySources: string[];
  purposeAlignment: number; // 0-100 scale
}

export interface JourneyContextData {
  lifeValues: LifeValues;
  moneyGoals: MoneyGoals;
  intrinsicDrives: IntrinsicDrives;
  updateLifeValues: (values: Partial<LifeValues>) => void;
  updateMoneyGoals: (goals: Partial<MoneyGoals>) => void;
  updateIntrinsicDrives: (drives: Partial<IntrinsicDrives>) => void;
  detectPatterns: () => IntrinsicDrives;
}

const JourneyContext = createContext<JourneyContextData | undefined>(undefined);

export const useJourneyContext = (): JourneyContextData => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourneyContext must be used within a JourneyProvider');
  }
  return context;
};

export const JourneyProvider = ({ children }: { children: ReactNode }) => {
  // Safely use useAuth with error boundary
  let user;
  try {
    const authResult = useAuth();
    user = authResult.user;
  } catch (error) {
    console.warn('useAuth failed in JourneyProvider, using fallback:', error);
    // Check if it's specifically the QueryClient error
    if (error instanceof Error && error.message.includes('No QueryClient set')) {
      console.warn('QueryClient not available yet, JourneyProvider will use fallback state');
    }
    user = undefined;
  }
  
  // Initialize with empty state
  const [lifeValues, setLifeValues] = useState<LifeValues>({
    relationships: [],
    personalStories: {
      childStory: '',
      professionalStory: '',
      kindnessReceived: '',
      agencyAtWork: ''
    },
    coreValues: [],
    authenticMoments: []
  });

  const [moneyGoals, setMoneyGoals] = useState<MoneyGoals>({
    leverage: {
      timeInvestments: [],
      valueCreation: [],
      agencyBuilding: []
    },
    lemonadeStandChoices: {
      friendsCalled: [],
      businessDecisions: [],
      ownershipPhilosophy: ''
    },
    financialVision: {
      timeFreedom: false,
      balanceSheetThinking: false,
      socialCapitalFocus: false
    }
  });

  const [intrinsicDrives, setIntrinsicDrives] = useState<IntrinsicDrives>({
    patterns: [],
    motivations: [],
    energySources: [],
    purposeAlignment: 0
  });

  // Pattern detection AI logic
  const detectPatterns = (): IntrinsicDrives => {
    const patterns: string[] = [];
    const motivations: string[] = [];
    const energySources: string[] = [];
    let purposeAlignment = 0;

    // Analyze LIFE stories for patterns
    const stories = Object.values(lifeValues.personalStories);
    stories.forEach(story => {
      if (story.toLowerCase().includes('help')) patterns.push('Helper/Supporter');
      if (story.toLowerCase().includes('lead')) patterns.push('Natural Leader');
      if (story.toLowerCase().includes('create')) patterns.push('Creator/Innovator');
      if (story.toLowerCase().includes('solve')) patterns.push('Problem Solver');
      if (story.toLowerCase().includes('teach')) patterns.push('Educator/Mentor');
      if (story.toLowerCase().includes('organize')) patterns.push('Organizer/Coordinator');
    });

    // Analyze MONEY choices for motivations
    const { friendsCalled, businessDecisions } = moneyGoals.lemonadeStandChoices;
    if (friendsCalled.length > 0) motivations.push('Collaborative Decision Making');
    if (businessDecisions.some(d => d.includes('quality'))) motivations.push('Quality Focus');
    if (businessDecisions.some(d => d.includes('relationship'))) motivations.push('Relationship Priority');
    if (businessDecisions.some(d => d.includes('profit'))) motivations.push('Results Oriented');

    // Analyze energy sources from values and moments
    lifeValues.authenticMoments.forEach(moment => {
      if (moment.toLowerCase().includes('team')) energySources.push('Teamwork');
      if (moment.toLowerCase().includes('alone')) energySources.push('Independent Work');
      if (moment.toLowerCase().includes('challenge')) energySources.push('Overcoming Challenges');
      if (moment.toLowerCase().includes('creative')) energySources.push('Creative Expression');
    });

    // Calculate purpose alignment based on consistency
    const valueConsistency = lifeValues.coreValues.length > 0 ? 25 : 0;
    const storyConsistency = stories.filter(s => s.length > 0).length * 15;
    const decisionConsistency = moneyGoals.financialVision.timeFreedom ? 20 : 0;
    const relationshipConsistency = lifeValues.relationships.length > 0 ? 15 : 0;
    purposeAlignment = valueConsistency + storyConsistency + decisionConsistency + relationshipConsistency;

    return {
      patterns: [...new Set(patterns)],
      motivations: [...new Set(motivations)],
      energySources: [...new Set(energySources)],
      purposeAlignment: Math.min(purposeAlignment, 100)
    };
  };

  // Update functions
  const updateLifeValues = (values: Partial<LifeValues>) => {
    setLifeValues(prev => ({ ...prev, ...values }));
  };

  const updateMoneyGoals = (goals: Partial<MoneyGoals>) => {
    setMoneyGoals(prev => ({ ...prev, ...goals }));
  };

  const updateIntrinsicDrives = (drives: Partial<IntrinsicDrives>) => {
    setIntrinsicDrives(prev => ({ ...prev, ...drives }));
  };

  const contextValue: JourneyContextData = {
    lifeValues,
    moneyGoals,
    intrinsicDrives,
    updateLifeValues,
    updateMoneyGoals,
    updateIntrinsicDrives,
    detectPatterns
  };

  return React.createElement(
    JourneyContext.Provider,
    { value: contextValue },
    children
  );
};